<template>
  <div
      :style="styles"
      class="cell"
      @click="cellAction"
  >
    <transition-group name="fade">
      <expandable-cell
          v-if="expand"
          :class="expandedClass"
          :data="expandedData"
          @refreshCalendar="$emit('refresh-calendar')"
      />
    </transition-group>
    <div
        v-if="data.name || data.default_name || data.id"
        class="cell-content"
    >
      <!-- SMS Status Indicators -->
      <div
          v-if="data.pixels >= oneLineCell && (data.smsSentCount > 0 || data.smsConfirmedCount > 0)"
          class="sms-indicators"
      >
        <span
            v-if="data.smsConfirmedCount > 0"
            class="sms-indicator sms-confirmed"
            :title="`Potwierdzono: ${data.smsConfirmedCount}/${data.clientsCount}`"
        >
          <font-awesome-icon icon="check" />
        </span>
        <span
            v-if="data.smsSentCount > 0"
            class="sms-indicator sms-sent"
            :title="`Wysłano: ${data.smsSentCount}/${data.clientsCount}`"
        >
          <font-awesome-icon icon="paper-plane" />
        </span>
      </div>
      <div
          v-if="data.pixels >= oneLineCell"
          class="text"
          :style="cellSize"
          ref="cellName"
      >
        <div
          v-if="data.name"
          class="text"
        >
          {{ data.name }}
        </div>
        <div
          v-else-if="data.default_name"
          class="text"
        >
          {{ data.default_name }}
        </div>
      </div>
      <div v-if="data.pixels >= threeLinesCell"
          class="text"
      >
        {{ start }}-{{ end }}
      </div>
      <div v-if='data.pixels >= twoLinesCell || data.isEmpty' class="icons">
        <div class="icons">
          <custom-icon-button
            v-if="!data.isEmpty"
            button-name="open-client-list"
            class="icon"
            iconName="clipboard-list"
            @openClientListButton="changeModalState"
          />
          <custom-icon-button
            v-if="!data.isEmpty && !data.isApproved && (isAdmin || canEditEvent)"
            button-name="open-edit-event"
            class="icon"
            iconName="edit"
            @openEditEventButton="changeEditModalState"
          />
          <custom-icon-button
            v-if="(isAdmin || canDeleteEvent) && !data.isApproved"
            button-name="remove-event"
            class="icon"
            iconName="trash"
            @RemoveEventButton="changeRemoveEventConfirm"
          />
          <teleport v-if="showClientModal" to="body">
            <suspense>
              <template #default>
                <clients-modal
                  :event-data="data"
                  @closeClientsModal="changeModalState"
                />
              </template>
              <template #fallback>
                <ring-loader
                  class="loader"
                  :color="colors.grey"
                  size="100px"
                />
              </template>
            </suspense>
          </teleport>
          <teleport v-if="showRemoveEventConfirm" to="body">
            <remove-modal
              text="Czy na pewno chesz usunąć zajęcia?"
              :remove-series="data.isPeriodic"
              @cancel="changeRemoveEventConfirm"
              @remove="removeEvent"
            />
          </teleport>
        </div>
      </div>
    </div>
    <teleport v-if="showCreateEventModal" to="body">
      <edit-event-modal
        @closeEditEventModal="changeEditModalState"
        :eventData="eventData"
        :times="times.values"
      />
    </teleport>
  </div>
</template>

<script>
import {
  computed, onBeforeUnmount, onMounted, reactive, ref, watch,
} from 'vue';
import RingLoader from 'vue-spinner/src/RingLoader.vue';
import axios from 'axios';

import store from '@/store';
import { urlBuilder } from '../../plugins/helpers';

import colors from '../../assets/css/colors.scss';

import ClientsModal from '../modal/ClientsModal.vue';
import CustomIconButton from '../common/CustomIconButton.vue';
import EditEventModal from '../modal/EditEventModal.vue';
import RemoveModal from '../modal/RemoveModal.vue';

export default {
  name: 'ExpandableCell',
  components: {
    RemoveModal,
    CustomIconButton,
    ClientsModal,
    RingLoader,
    EditEventModal,
  },
  props: {
    data: Object,
  },

  setup(props, { emit }) {
    const oneLineCell = 30;
    const twoLinesCell = 60;
    const threeLinesCell = 90;
    const cellPadding = 2;

    const end = ref('');
    const start = ref('');

    const canAddEvent = computed(() => store.getters.canAddEvent);
    const canDeleteEvent = computed(() => store.getters.canDeleteEvent);
    const canEditEvent = computed(() => store.getters.canEditEvent);
    const isAdmin = computed(() => store.getters.isAdmin);

    if (props.data.start && props.data.end) {
      end.value = props.data.end.format('HH:mm');
      start.value = props.data.start.format('HH:mm');
    }

    const cellName = ref(null);
    const isPointer = ref(false);

    const expand = ref(false);

    onMounted(() => {
      if (
        props.data.name && !expand.value && cellName.value
        && (props.data.pixels < threeLinesCell
        || cellName.value.clientHeight < cellName.value.scrollHeight)) {
        isPointer.value = true;
      }
    });

    const styles = computed(() => ({
      height: `${props.data.pixels}px`,
      background: `${props.data.color}FF`,
      border: props.data.isApproved ? '2px solid #0FEC13' : `2px solid ${props.data.fontColor}`,
      color: props.data.fontColor ? props.data.fontColor : 'black',
      cursor: isPointer.value ? 'pointer' : '',
      pointerEvent: props.data.name ? 'auto' : 'none',
      opacity: props.data.isOpacity ? '0%' : '',
    }));

    const cellSize = computed(() => {
      let maxHeight = '';
      if (props.data.pixels >= twoLinesCell && props.data.pixels <= threeLinesCell) {
        maxHeight = `${props.data.pixels - oneLineCell - cellPadding}px`;
      } else if (props.data.pixels >= threeLinesCell) {
        maxHeight = `${props.data.pixels - twoLinesCell - cellPadding}px`;
      } else maxHeight = `${props.data.pixels}px`;
      return { maxHeight };
    });

    const expandedData = reactive({});
    let windowWidth = ref(window.innerWidth);

    function handleResize() {
      windowWidth = window.innerWidth;
    }

    window.addEventListener('resize', handleResize);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    function createExpandedData() {
      const newData = { ...props.data };
      if (!cellName.value) return;
      if (props.data.pixels < threeLinesCell
        || (cellName.value.clientHeight < cellName.value.scrollHeight)) {
        const textHeight = Math.ceil(
          (cellName.value.scrollHeight - cellName.value.clientHeight) / oneLineCell,
        ) * oneLineCell;
        if (props.data.pixels < oneLineCell) {
          newData.pixels = props.data.pixels + threeLinesCell + textHeight;
        } else if (props.data.pixels < twoLinesCell) {
          newData.pixels = props.data.pixels + twoLinesCell + textHeight;
        } else if (props.data.pixels < threeLinesCell) {
          newData.pixels = props.data.pixels + oneLineCell + textHeight;
        } else { newData.pixels = textHeight + props.data.pixels; }
        Object.assign(expandedData, newData);
      }
    }

    const showCreateEventModal = ref(false);

    async function changeEditModalState() {
      if (showCreateEventModal.value) {
        await store.dispatch('resetChangeEvent');
        emit('refresh-calendar');
      }
      showCreateEventModal.value = !showCreateEventModal.value;
    }

    function parseIntHourToStr(hour) {
      return hour < 10 ? `0${hour}` : `${hour}`;
    }

    const times = reactive({ values: {} });

    function getTime(heightClick) {
      const clickStartMinutes = heightClick % 60;
      const clickStartHour = (heightClick - clickStartMinutes) / 60 + 8;
      let strStartMinutes;
      let strEndMinutes;
      let endHour;
      switch (true) {
        case clickStartMinutes >= 45:
          strStartMinutes = '45';
          strEndMinutes = '30';
          endHour = clickStartHour + 1;
          break;
        case clickStartMinutes >= 30 && clickStartMinutes < 45:
          strStartMinutes = '30';
          strEndMinutes = '15';
          endHour = clickStartHour + 1;
          break;
        case clickStartMinutes >= 15 && clickStartMinutes < 30:
          strStartMinutes = '15';
          strEndMinutes = '00';
          endHour = clickStartHour + 1;
          break;
        default:
          strStartMinutes = '00';
          strEndMinutes = '45';
          endHour = clickStartHour;
      }
      const strStartHour = parseIntHourToStr(clickStartHour);
      const strEndHour = parseIntHourToStr(endHour);
      times.values = {
        startTime: `${strStartHour}:${strStartMinutes}`,
        endTime: `${strEndHour}:${strEndMinutes}`,
      };
    }

    const eventData = reactive({
      date: props.data.date,
      trainer: props.data.trainerId ? props.data.trainerId : store.getters.selectedTrainerId,
      id: props.data.id ? props.data.id : null,
    });

    async function changeStoreEventState() {
      if (store.getters.expandedEvent && store.getters.expandedEvent !== eventData.id) {
        await store.dispatch('changeExpandedEvent', eventData.id);
        await store.dispatch('changeCloseEvent', true);
      } else if (store.getters.expandedEvent && store.getters.expandedEvent === eventData.id) {
        await store.dispatch('changeExpandedEvent', null);
      } else if (!store.getters.expandedEvent) {
        await store.dispatch('changeExpandedEvent', eventData.id);
      }
    }

    async function cellAction(e) {
      const heightClick = e.pageY - 96;
      getTime(heightClick);
      if (props.data.openable) {
        if (!expand.value) {
          await changeStoreEventState();
          createExpandedData();
        }
        expand.value = !expand.value;
      } else if (isAdmin.value || canAddEvent.value) {
        changeEditModalState();
      }
    }

    const expandedClass = computed(() => {
      if (props.data.toEnd + 30 >= expandedData.pixels) {
        return 'expanded-down';
      }
      return 'expanded-up';
    });

    const showClientModal = ref(false);

    function changeModalState(event) {
      if (showClientModal.value && event.refresh) emit('refresh-calendar');
      showClientModal.value = !showClientModal.value;
    }

    async function removeEvent(event) {
      const payload = {
        charge: event,
      };
      let params = null;
      if (event.removeWithPeriodic) params = { start_date: props.data.date.format('YYYY-MM-DD') };
      await axios.delete(urlBuilder(`/events/${props.data.id}/`, params), { data: payload });
      await store.dispatch('changeCloseEvent', false);
      await store.dispatch('changeExpandedEvent', null);

      emit('refresh-calendar');
    }

    const showRemoveEventConfirm = ref(false);

    function changeRemoveEventConfirm() {
      showRemoveEventConfirm.value = !showRemoveEventConfirm.value;
    }

    watch(() => store.getters.closeEvent, async () => {
      if (store.getters.closeEvent && expand.value) {
        expand.value = !expand.value;
        await store.dispatch('changeCloseEvent', false);
      }
    });

    return {
      canDeleteEvent,
      canEditEvent,
      cellAction,
      cellName,
      cellSize,
      changeEditModalState,
      changeModalState,
      changeRemoveEventConfirm,
      colors,
      end,
      eventData,
      expand,
      expandedClass,
      expandedData,
      isAdmin,
      oneLineCell,
      removeEvent,
      showClientModal,
      showCreateEventModal,
      showRemoveEventConfirm,
      start,
      styles,
      times,
      threeLinesCell,
      twoLinesCell,
      windowWidth,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/colors.scss';

.cell {
  position: relative;
  border-radius: 15px;
  max-width: calc((100vw - 72px) / 7);
  min-width: 110px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s ease-in-out;
}

.fade-enter-from, .fade-leave-to{
  opacity: 0;
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

.expanded-down {
  margin: -2px 0 0 -2px;
  position: absolute;
  width: calc(100% + 4px);
  z-index: 10;
}

.expanded-up {
  margin: 0 0 -2px -2px;
  position: absolute;
  width: calc(100% + 4px);
  bottom: 0;
  z-index: 10;
}

.cell-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.text {
  line-height: 1.7;
  max-height: 120px;
  max-width: 100%;
  overflow: hidden;
  padding: 0 5px 0 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icons {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
}

.icon {
  align-self: center;
  background-color: white;
  border-radius: 90px;
  box-shadow: 1px 1px 1px 1px $grey;
  color: $grey;
  height: 25px;
  padding: 4px;
  transition: background-color .2s;
  width: 25px;
}

.icon:hover {
  background-color: $light-grey;
  transition: background-color .2s;
}

.loader {
  align-items: center;
  bottom: 50%;
  display: flex;
  justify-content: center;
  left: 50%;
  position: fixed;
  right: 50%;
  top: 50%;
  z-index: 20;
}

.sms-indicators {
  display: flex;
  gap: 3px;
  justify-content: center;
  padding-top: 2px;
}

.sms-indicator {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  font-size: 10px;
  font-weight: bold;
  height: 16px;
  justify-content: center;
  width: 16px;
}

.sms-sent {
  background-color: #17a2b8;
  color: white;
}

.sms-confirmed {
  background-color: #28a745;
  color: white;
}
</style>
